import {
    STATE_PROCESSING,
    STATE_HALTED,
    MESSAGE_TYPE_DEBUG,
    MESSAGE_TYPE_INFO,
    STATE_RUNNING,
    MESSAGE_TYPE_ERROR,
    STATE_ERROR,
    STATE_SUCCESS
} from './constants';
import Log from "./logger";
import axios from "axios";
import Auth from "./auth";

const api = axios.create();

function createEmptyChallenge() {
    return {
        id: null,
        expiresAt: null,
        authType: "jwt",
        authImages: {},
        authImagesData: {},
        authTokenUrl: null,
        pollingUrl: null,
        socketsUrl: null
    }
}

class Worker {

    static workerInstances = {};
    authInstances = [];

    challenge = createEmptyChallenge(); // Always create a new Object (avoid references)

    cancelTokenSource = null;

    constructor(key) {

        if (key in Worker.workerInstances) {
            return Worker.workerInstances[key];
        }

        Worker.workerInstances[key] = this;
    }

    async start(instanceId) {

        if (this.authInstances.indexOf(instanceId) < 0) {
            this.authInstances.push(instanceId);
        }

        if (this.authInstances.length > 1) {

            this.debug("Added Instance \"%s\".".replace("%s", instanceId));

            // Do we have a challenge already?
            if (this.challenge.id) {
                this.dispatchEvent(STATE_RUNNING, this.challenge);
            } else {
                this.dispatchEvent(STATE_PROCESSING);
            }

            return;
        }

        this.debug("Starting Worker for \"%s\".".replace("%s", instanceId));
        this.dispatchEvent(STATE_PROCESSING);

        this.challenge = createEmptyChallenge();
        this.cancelTokenSource = axios.CancelToken.source();

        let instance = Auth.getInstance(instanceId);

        try {
            this.challenge = await this.createChallenge(instance.getApi(), instance.getKey(), instance.getAuthType());

        } catch (error) {

            // Check is error is a canceled request
            if (axios.isCancel(error) || error.code === "ECONNABORTED") {
                this.dispatchEvent(STATE_HALTED);
                return;
            }

            this.dispatchEvent(STATE_ERROR, error.response.data);

            return;
        }

        this.debug("Got Challenge \"%s\"".replace("%s", this.challenge.id));
        this.dispatchEvent(STATE_RUNNING, this.challenge);

        let pollingActive = true;

        do {
            this.debug("Ping ...");

            try {
                let challengeResponse = await this.getChallenge(this.challenge.pollingUrl);

                if (challengeResponse.status === 200) {
                    this.dispatchEvent(STATE_PROCESSING);
                    let token = challengeResponse.data.token;

                    // Do this only in development
                    if (_IS_DEVELOPMENT) {
                        let [h, p, s] = token.split(".");
                        let claims = JSON.parse(atob(p));
                        this.debug("Got token for identifier \"%s\"".replace("%s", claims['sub']));
                    }

                    if (instance.getAuthUrl()) {
                        // Start the client authentication asynchronously
                        this.tryAuthentication(instance.getAuthUrl(), instance.getAuthHeader(), token)
                            .then((response) => {
                                this.dispatchEvent(STATE_SUCCESS, token);
                                if (response.data.location) {
                                    window.location.replace(response.data.location);
                                } else if (response.request.responseURL) {
                                    window.location.replace(response.request.responseURL);
                                }
                            })
                            .catch((error) => {
                                this.dispatchEvent(STATE_ERROR, error.message);
                                this.log("Login failed. The controller at \"%c\" responded with \"%s\"."
                                    .replace("%c", instance.getAuthUrl())
                                    .replace("%s", error.message), MESSAGE_TYPE_ERROR);
                            })
                            .finally(() => {
                                this.authInstances = [];
                                this.challenge = createEmptyChallenge()
                            })

                    } else {
                        this.dispatchEvent(STATE_SUCCESS, token);

                        this.authInstances = [];
                        this.challenge = createEmptyChallenge()
                    }

                    if (this.cancelTokenSource) this.cancelTokenSource.cancel();
                    this.cancelTokenSource = null;

                    pollingActive = false;
                }

            } catch (error) {

                // Check is error is a canceled request
                if (axios.isCancel(error) || error.code === "ECONNABORTED") {
                    this.dispatchEvent(STATE_HALTED);
                } else {

                    // Need to check if error has a response
                    this.dispatchEvent(STATE_ERROR, error.response ? error.response.data : error);
                }

                pollingActive = false;
            }

        } while (pollingActive);

    }

    stop(instanceId) {

        if (this.authInstances.length > 1) {

            this.debug("Removing Instance \"%s\". Worker still running.".replace("%s", instanceId));
            this.dispatchEvent(STATE_HALTED);
            this.authInstances = this.authInstances.filter(item => item !== instanceId);

            return;
        }

        this.debug("Removing Instance \"%s\".".replace("%s", instanceId));
        this.debug("Stopping Worker for \"%s\".".replace("%s", instanceId));
        this.dispatchEvent(STATE_HALTED);
        this.authInstances = [];

        if (this.cancelTokenSource) this.cancelTokenSource.cancel();
        this.cancelTokenSource = null;
    }

    createChallenge(apiUrl, key, authType = "jwt") {
        return api.post(apiUrl + "/v1/entity/challenges", {
            key: key,
            authType: authType,
            returnAuthImageData: true
        }, {
            cancelToken: this.cancelTokenSource.token
        }).then(response => {
            if (!response.data.id) {
                throw new Error("Unexpected response from API.")
            }

            return response.data;
        })
    }

    getChallenge(pollingUrl) {
        return api.get(pollingUrl, {
            cancelToken: this.cancelTokenSource.token
        });
    }

    tryAuthentication(authUrl, authHeader, token) {
        let requestHeaders = {
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest"
        };

        requestHeaders[authHeader] = (authHeader.toLowerCase() === "authorization") ? "Bearer " + token : token;

        return axios.get(authUrl, {
            withCredentials: true,
            headers: requestHeaders
        });
    }

    dispatchEvent(type, payload = {}) {
        this.authInstances.forEach(i => {
            Auth.getInstance(i).handleWorkerEvents(i, type, payload);
        });
    }

    debug(message) {
        return this.log(message, MESSAGE_TYPE_DEBUG);
    }

    log(message, type = MESSAGE_TYPE_INFO) {
        const entry = "[Almefy][Worker]%s ".replace("%s", this.key ? "[%s]".replace("%s", this.key) : "") + message;
        Log.add(entry, type);

        return this;
    }

}

export default Worker